import React, { ChangeEvent, FC, useEffect, useRef } from "react";
import { useController, UseFormSetValue, useWatch } from "react-hook-form";
import { useUpdateEffect } from "react-use";
import { Button } from "src/components/ui/button";

interface Props {
	stepNumber: number;
	control: any;
	error?: boolean;
	upload: (file: File, uploadType: string, uploadIndex?: number) => void;
	uploading?: boolean;
	uuid: string[];
	setMediasPreview: React.Dispatch<React.SetStateAction<Media[]>>;
	deleteAllMedia: () => void;
	setValue: UseFormSetValue<BannerFormState>;
}

const MultiMediaUpload: FC<Props> = ({
	stepNumber,
	control,
	upload,
	error,
	uploading,
	uuid,
	setMediasPreview,
	deleteAllMedia,
	setValue
}) => {
	const inputRef = useRef<HTMLInputElement>(null);

	const {
		field: { onChange: uuidOnChange, value: uuidValue, ...inputProps }
	} = useController({
		name: `steps.${stepNumber - 1}.value`,
		control,
		rules: { required: false }
	});

	const {
		field: { onChange: fileNameOnChange }
	} = useController({
		name: `steps.${stepNumber - 1}.file`,
		control
	});

	const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
		const target = event.target as HTMLInputElement;

		if (!target) return;
		if (!target.files) return;

		const selectedFiles = Array.from(target.files);

		if (
			selectedFiles.length > 3 ||
			selectedFiles.length + uuid.filter((id) => id).length > 3
		) {
			return alert(
				"You are only allowed to upload a maximum of 3 files at a time"
			);
		}

		if (
			selectedFiles.filter((file) => file.type.includes("video")).length > 1
		) {
			return alert("Only one video can be added to the post");
		}

		selectedFiles.forEach((file) => {
			const reader = new FileReader();

			reader.onload = (progressEvent: ProgressEvent<FileReader>) => {
				if (!progressEvent) return;

				if (!progressEvent.target) return;

				setValue(`steps.${stepNumber - 1}.file`, file.type);

				setMediasPreview((prevState) => [
					...prevState,
					{
						preview: progressEvent!.target!.result as string,
						type: file.type.includes("video") ? "video" : "image",
						file: file.name,
						value: "",
						id: Math.random()
					}
				]);

				if (file && file.type.includes("image")) {
					upload(file, "images");
				}

				if (file && file.type.includes("video")) {
					upload(file, "video");
				}
			};

			reader.readAsDataURL(file);
		});

		event.target.value = "";
	};

	const medias: Media[] | undefined = useWatch({
		name: `steps.${stepNumber - 1}.mediasPreview`,
		control
	});

	const sanitizedMedias =
		medias && medias.filter((media) => media.preview && media.type);

	const isVideoUploaded =
		sanitizedMedias &&
		sanitizedMedias.some((item: Media) => item.type.includes("video"));

	const getInputText = () => {
		if (uploading) return "Uploading...";

		return "Upload Media";
	};

	useUpdateEffect(() => {
		if (!uuid) return;

		setValue(`steps.${stepNumber - 1}.value`, String(uuid));
		uuidOnChange(uuid);
	}, [uuid]);

	return (
		<div className="w-[200px]">
			{((sanitizedMedias && sanitizedMedias.length === 3) || error) && (
				<Button
					type="button"
					variant="outline"
					className="w-full"
					onClick={deleteAllMedia}
					disabled={uploading || (uuid.length !== 3 && !error)}
				>
					Delete all media
				</Button>
			)}

			{sanitizedMedias && sanitizedMedias.length < 3 && !error && (
				<div>
					<input
						type="file"
						onChange={onFileSelected}
						accept={
							isVideoUploaded
								? "image/jpeg,image/png"
								: "image/jpeg,image/png,video/mp4,video/quicktime"
						}
						{...inputProps}
						name={`steps.${stepNumber - 1}.value`}
						ref={inputRef}
						style={{ display: "none" }}
						aria-label="file-upload"
						multiple={true}
					/>

					<Button
						type="button"
						variant="outline"
						className="w-full"
						onClick={() => inputRef?.current?.click()}
						disabled={uploading}
					>
						{getInputText()}
					</Button>
				</div>
			)}
		</div>
	);
};

export default MultiMediaUpload;
